import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * LandingLoader — Luni-style "Creation of the X..." boot sequence.
 * Plays once per session. Counts $0 → final value over ~3.2s, then fades out.
 *
 * Mobile-aware: cuts to smaller type and shorter duration on narrow screens
 * so we don't make people wait staring at a loader on a phone.
 */
const TARGET = 2847302;          // "$2,847,302 settled" — total to count to
const SUFFIX = " settled";
const DURATION_MS = 3200;
const MOBILE_DURATION_MS = 2000;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const LandingLoader = ({ onDone }) => {
  const [value, setValue] = useState(0);
  const [pct, setPct] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const dur = isMobile ? MOBILE_DURATION_MS : DURATION_MS;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = easeOutCubic(t);
      setValue(Math.round(TARGET * eased));
      setPct(Math.round(100 * eased));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        // Hold at 100 for a beat, then signal completion
        setTimeout(() => {
          setVisible(false);
          setTimeout(() => onDone?.(), 600);
        }, 350);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onDone]);

  const formatted = value.toLocaleString("en-US");

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="landing-loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
          className="fixed inset-0 z-[100] landing-v2 flex flex-col justify-end"
        >
          {/* Brand mark top-left */}
          <div className="absolute top-6 left-6 md:top-8 md:left-10 flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center l2-display text-base"
              style={{ background: "var(--l2-ink)", color: "var(--l2-cream)" }}
              data-testid="loader-mark"
            >
              Q
            </div>
            <span className="l2-display text-sm md:text-base">Quill</span>
          </div>

          {/* Caption bottom-left */}
          <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 l2-mono text-xs md:text-sm">
            Compiling your ledger…
          </div>

          {/* Big counter bottom-right */}
          <div className="absolute bottom-4 right-4 md:bottom-8 md:right-10 text-right">
            <div
              className="l2-display tabular-nums"
              style={{
                fontSize: "clamp(72px, 14vw, 220px)",
                color: "var(--l2-ink)",
              }}
              data-testid="loader-amount"
            >
              ${formatted}
            </div>
            <div
              className="l2-mono mt-1 md:mt-2"
              style={{
                fontSize: "clamp(11px, 1.2vw, 14px)",
                color: "var(--l2-ink-2)",
              }}
            >
              <span className="tabular-nums">{pct}</span>%{SUFFIX} so far
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LandingLoader;
